import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonIcon from '@mui/icons-material/Person';
import { IconButton, Menu, MenuItem, Stack, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { ReactComponent as LinkedInIcon } from "../../../assets/icons/linkedin.svg";
import {DashboardContext, AnsweredQuestion } from '../../../contexts/DashboardContext';
import useContentCopy from '../../../hooks/useContentCopy';
import theme from '../../../theme';
import ConfirmDialog from '../../modals/ConfirmDialog';
import AddCustomPeopleModal from '../../modals/dashboard-queries/AddCustomPeopleModal';
import {AuthContext} from '../../../contexts/AuthContext';
import { addDashboardQueryFunc } from '../../../lib/helper';
import { DashboardQuerySource } from '../../../types/files';
import { AnswerQuestionResponseType } from '../../../types/search';

const useStyles = makeStyles((theme) => ({
    masonryElement: {
        display: 'flex',
        width: '90%',
        height: 60,
        overflow: 'hidden',
        padding: '8px 16px',
        borderRadius: 16,
        border: `2px solid ${theme.colors.primary['100']}`,
        background: theme.colors.primary['50'],
        transition: 'all 0.2s ease',
        "&:hover": {
            border: `2px solid ${theme.colors.primary['200']}`,
            background: theme.colors.primary['50'],
            boxShadow: '0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px rgba(0, 0, 0, 0.14), 0px 1px 5px rgba(0, 0, 0, 0.12)',
        },
    },
    personIcon: {
        width: 32,
        height: 32,
        fill: theme.palette.primary.main,
    },
    icon: {
        width: 24,
        height: 24,
        fill: '#7bd4d4',
    },
    link: {
        display: 'inline-block',
        maxWidth: '14ch',
        fontFamily: 'Inter !important',
        fontSize: '1rem',
        fontWeight: 'bold',
        textDecoration: 'none',
        color: '#666666',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        lineClamp: 1,
        "&:hover": {
            textDecoration: 'none !important',
        },
    },
    moreIcon: {
        padding: 'unset',
        color: theme.colors.neutral['500'],
        "& > svg": {
            width: 20,
            height: 20,
        },
    },
}));

const KeyPeopleAnswer: React.FC<{
    keyPeople?: AnsweredQuestion|null,
    onKeyPeopleCount?: (count: number) => void,
}> = ({ keyPeople, onKeyPeopleCount }) => {
    const classes = useStyles();
    const { userGroup } = useContext(AuthContext);
    const { dashboard, isPublicView } = useContext(DashboardContext);
    const { dashboardQueries, defaultQueryTemplateValues } = useContext(DashboardContext);
    const { getCopiableId } = useContentCopy();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [addCustomPeopleModalOpen, setAddCustomPeopleModalOpen] = useState<boolean>(false);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState<boolean>(false);
    const [focusItem, setFocusItem] = useState<{title: string, link?: string}|undefined>(undefined);

    const keyPeopleAnswer = keyPeople?.answer as AnsweredQuestion[] || [];
    const copiableId = getCopiableId('Key People');
    const extendCopyId = getCopiableId('extend');

    const handleDelete = useCallback(() => {
        const peopleQuery = dashboardQueries.find(query => query.title === 'Key People');
        const peopleAnswers = JSON.parse(peopleQuery?.answer || '{}')?.answer as typeof focusItem[];
        let newAnswer: any[] = [];

        if (!!focusItem && Array.isArray(peopleAnswers)) {
            newAnswer = [...peopleAnswers].filter(people => people?.title !== focusItem?.title);
            addDashboardQueryFunc(
                dashboard!.id,
                'Key People',
                userGroup!,
                dashboard!.title,
                JSON.stringify({
                    answer: newAnswer,
                    type: AnswerQuestionResponseType.URL_LIST,
                }),
                undefined,
                defaultQueryTemplateValues.find(defQuery => defQuery.title === 'Key People')?.order || 1,
                true,
                DashboardQuerySource.MANUAL,
            ).then(() => setConfirmDeleteOpen(false));
        } else {
            setConfirmDeleteOpen(false);
        }
    // eslint-disable-next-line
    }, [dashboard, focusItem, defaultQueryTemplateValues, dashboardQueries, userGroup]);

    useEffect(() => onKeyPeopleCount?.(keyPeopleAnswer.length)
    // eslint-disable-next-line
    , [keyPeopleAnswer]);

    if (!Array.isArray(keyPeople?.answer) || !keyPeopleAnswer?.length)
        return (<></>);

    return (<>
        <Stack id={copiableId} width="100%">
            <Stack id={extendCopyId} direction="column" spacing={2} alignItems="stretch" width="100%" px={2}>
                {keyPeopleAnswer?.map((element: any, i) => (
                    <Stack className={classes.masonryElement} alignItems="flex-start" justifyContent="center"
                        key={'key-people-answer-87-' + i}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" minWidth="80%"
                                sx={{ cursor: !!element.link ? 'pointer' : 'auto' }}
                                onClick={!!element.link ? (e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    window.open(element.link, '_blank', 'noopener, noreferrer');
                                } : undefined}>
                                <PersonIcon className={classes.personIcon} />
                                <Typography className={classes.link}>
                                    {element.title.split('|')?.[0]?.split('-')?.[0]}
                                </Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" justifyContent="flex-end" width="25%">
                                {!!element.link && element.link?.includes('linkedin') && (
                                    <IconButton onClick={(e) =>  {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        window.open(element.link, '_blank', 'noopener, noreferrer');
                                    }} disableRipple>
                                        <LinkedInIcon className={classes.icon} />
                                    </IconButton>
                                )}
                                {!isPublicView && (
                                    <IconButton className={classes.moreIcon}
                                        onClick={(e) =>  {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            setAnchorEl(e.currentTarget);
                                            setFocusItem(element);
                                        }} disableRipple> <MoreVertIcon /> </IconButton>
                                )}
                                <Menu open={Boolean(anchorEl)}
                                    anchorEl={anchorEl}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                                    onClose={() => setAnchorEl(null)}>
                                    <MenuItem onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        setAnchorEl(null);
                                        setAddCustomPeopleModalOpen(true);
                                    }}> {'Edit'} </MenuItem>
                                    <MenuItem sx={{ color: theme.colors.error['500']}}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            setAnchorEl(null);
                                            setConfirmDeleteOpen(true);
                                    }}> {'Delete'} </MenuItem>
                                </Menu>
                            </Stack>
                        </Stack>
                    </Stack>
                ))}
            </Stack>
        </Stack>
        {addCustomPeopleModalOpen && (
            <AddCustomPeopleModal
                isOpen={addCustomPeopleModalOpen}
                customValues={focusItem}
                onClose={() => {
                    setAddCustomPeopleModalOpen(false);
                }}>
            </AddCustomPeopleModal>
        )}
        {confirmDeleteOpen && (
            <ConfirmDialog
                title="Delete confirmation"
                content="Are you sure you want to delete this person?"
                open={confirmDeleteOpen}
                confirmCallback={handleDelete}
                cancelCallback={() => setConfirmDeleteOpen(false)}
            />
        )}
    </>);
}

export default KeyPeopleAnswer;
